/**
 * Profile Service
 * 
 * Provides functionality to update user profile information including:
 * - Name/Display Name
 * - Email
 * - Password
 * 
 * @module ProfileService
 */

import {
  updateProfile,
  updateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  User as FirebaseUser,
} from 'firebase/auth';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase/client';

export class ProfileService {
  /**
   * Update user first name and last name
   * 
   * @param userId - User ID (Firestore document ID)
   * @param firstName - New first name
   * @param lastName - New last name
   * @returns Promise resolving to success result
   */
  static async updateName(
    userId: string,
    firstName: string,
    lastName: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('User not authenticated');
      }

      const displayName = `${firstName} ${lastName}`.trim();

      // Update Firebase Auth profile
      await updateProfile(firebaseUser, { displayName });

      // Update Firestore user document
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        fullName: displayName,
        displayName: displayName,
        updatedAt: serverTimestamp(),
      });

      return { success: true };
    } catch (error: any) {
      console.error('Error updating name:', error);
      return {
        success: false,
        error: error.message || 'Failed to update name',
      };
    }
  }

  /**
   * Update user display name (legacy method - kept for backward compatibility)
   * 
   * @param userId - User ID (Firestore document ID)
   * @param displayName - New display name
   * @returns Promise resolving to success result
   */
  static async updateDisplayName(
    userId: string,
    displayName: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('User not authenticated');
      }

      // Update Firebase Auth profile
      await updateProfile(firebaseUser, { displayName });

      // Update Firestore user document
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        fullName: displayName,
        displayName: displayName,
        updatedAt: serverTimestamp(),
      });

      return { success: true };
    } catch (error: any) {
      console.error('Error updating display name:', error);
      return {
        success: false,
        error: error.message || 'Failed to update display name',
      };
    }
  }

  /**
   * Update user email
   * Requires reauthentication for security
   * 
   * @param userId - User ID (Firestore document ID)
   * @param newEmail - New email address
   * @param currentPassword - Current password for reauthentication
   * @returns Promise resolving to success result
   */
  static async updateEmail(
    userId: string,
    newEmail: string,
    currentPassword: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser || !firebaseUser.email) {
        throw new Error('User not authenticated');
      }

      // Reauthenticate user
      const credential = EmailAuthProvider.credential(
        firebaseUser.email,
        currentPassword
      );
      await reauthenticateWithCredential(firebaseUser, credential);

      // Update email in Firebase Auth
      await updateEmail(firebaseUser, newEmail);

      // Update email in Firestore
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        email: newEmail,
        updatedAt: serverTimestamp(),
      });

      return { success: true };
    } catch (error: any) {
      console.error('Error updating email:', error);
      let errorMessage = 'Failed to update email';
      
      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Current password is incorrect';
      } else if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already in use';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Please log out and log back in before changing your email';
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Update user password
   * Requires reauthentication for security
   * 
   * @param currentPassword - Current password for reauthentication
   * @param newPassword - New password
   * @returns Promise resolving to success result
   */
  static async updatePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser || !firebaseUser.email) {
        throw new Error('User not authenticated');
      }

      // Reauthenticate user
      const credential = EmailAuthProvider.credential(
        firebaseUser.email,
        currentPassword
      );
      await reauthenticateWithCredential(firebaseUser, credential);

      // Update password
      await updatePassword(firebaseUser, newPassword);

      return { success: true };
    } catch (error: any) {
      console.error('Error updating password:', error);
      let errorMessage = 'Failed to update password';
      
      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Current password is incorrect';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters';
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Please log out and log back in before changing your password';
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }
  }
}

